// app/javascript/controllers/form_controller.js
import { Controller } from "@hotwired/stimulus";

// action.to_s.gsub(/\W/, '_').split(/\/|_/).map(&:capitalize).join
function camelizeActionForJs(action) {
  return (action || "")
    .toString()
    .replace(/\W/g, "_")
    .split(/\/|_/)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

// Get a fresh reCAPTCHA v3 token and ensure it is written
// into the hidden input *inside the provided root element* (the form).
async function recaptchaGetFreshTokenFor(action, rootEl, { timeoutMs = 8000 } = {}) {
  const root = rootEl || document;
  const helperName = `executeRecaptchaFor${camelizeActionForJs(action)}Async`;
  const helper = window[helperName];
  const sel = `input[name="g-recaptcha-response-data[${action}]"],textarea[name="g-recaptcha-response-data[${action}]"]`;

  const timer = new Promise((_, rej) =>
    setTimeout(() => rej(new Error("recaptcha execute timeout")), timeoutMs)
  );

  // 1) Try the gem’s helper (usually populates the hidden input too)
  try {
    if (typeof helper === "function") {
      const token = await Promise.race([helper(), timer]);
      const input = root.querySelector(sel);
      if (input?.value) return input.value;
      if (token) { if (input) input.value = token; return token; }
    }
  } catch (e) {
    console.warn("[reCAPTCHA] helper execute error:", e);
  }

  // 2) Fallback: grecaptcha.ready + direct execute
  try {
    if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
      await new Promise(res => window.grecaptcha.ready(res));
      const siteKey =
        document.querySelector('meta[name="recaptcha-site-key"]')?.content ||
        window.RECAPTCHA_SITE_KEY ||
        window.recaptchaSiteKey;

      if (!siteKey) {
        console.warn("[reCAPTCHA] No site key available for direct execute.");
        return "";
      }

      const token = await window.grecaptcha.execute(siteKey, { action });
      const input = root.querySelector(sel);
      if (input) input.value = token;
      return token;
    }
  } catch (e) {
    console.warn("[reCAPTCHA] direct execute failed:", e);
  }

  return ""; // let caller decide how to handle
}

export default class extends Controller {
  static targets = ["selectAllButton", "productCheckbox", "errorContainer", "submitButton"];

  // The Stimulus controller is attached to the <form>, so this.element is the form.
  get form() { return this.element; }

  connect() {
    this.captureUTMParamsOnce();
    this.applyStoredUTMParamsToForm();
    this.captureIPAddress();
    this.captureReferrer();
  }

  // ----- Referrer / IP / UTM -----
  captureReferrer() {
    const referrer = document.referrer;
    if (referrer && !localStorage.getItem("referrer")) localStorage.setItem("referrer", referrer);

    let referrerField = this.form.querySelector("input[name='referrer']");
    if (!referrerField) {
      referrerField = document.createElement("input");
      referrerField.type = "hidden";
      referrerField.name = "referrer";
      this.form.appendChild(referrerField);
    }
    referrerField.value = localStorage.getItem("referrer") || "";
  }

  captureIPAddress() {
    fetch("https://api.ipify.org?format=json")
      .then(r => r.json())
      .then(data => {
        let ipAddressField = this.form.querySelector("input[name='ipAddress']");
        if (!ipAddressField) {
          ipAddressField = document.createElement("input");
          ipAddressField.type = "hidden";
          ipAddressField.name = "ipAddress";
          this.form.appendChild(ipAddressField);
        }
        ipAddressField.value = data.ip;
      })
      .catch(err => console.error("Error fetching IP address:", err));
  }

  captureUTMParamsOnce() {
    const urlParams = new URLSearchParams(window.location.search);
    ["utm_campaign", "utm_medium", "utm_source"].forEach(param => {
      const value = urlParams.get(param);
      if (value) localStorage.setItem(param, value);
    });

    // HubSpot hutk (best effort)
    let hutkMatch = document.cookie.match(/hubspotutk=([a-zA-Z0-9-_]+);?/);
    if (hutkMatch) {
      localStorage.setItem("hutk", hutkMatch[1]);
    } else {
      setTimeout(() => {
        hutkMatch = document.cookie.match(/hubspotutk=([a-zA-Z0-9-_]+);?/);
        if (hutkMatch) localStorage.setItem("hutk", hutkMatch[1]);
      }, 1000);
    }

    this.applyStoredUTMParamsToForm();
  }

  applyStoredUTMParamsToForm() {
    ["utm_campaign", "utm_medium", "utm_source"].forEach(param => {
      const value = localStorage.getItem(param) || "";
      let input = this.form.querySelector(`input[name='${param}__c']`);
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `${param}__c`;
        this.form.appendChild(input);
      }
      input.value = value;
    });

    try {
      console.log("✅ UTM Parameters applied to this form:", {
        utm_campaign: this.form.querySelector("input[name='utm_campaign__c']").value,
        utm_medium:   this.form.querySelector("input[name='utm_medium__c']").value,
        utm_source:   this.form.querySelector("input[name='utm_source__c']").value,
      });
    } catch (_) {}
  }

  // ----- Submit -----
  async submitForm(event) {
    event.preventDefault();
    const form = this.form;

    // Require at least one product if targets exist (fail-open if none defined)
    if (this.hasProductCheckboxTarget && !this.productCheckboxTargets.some(cb => cb.checked)) {
      if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.remove("hidden");
      return;
    }
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.add("hidden");

    const btn = this.hasSubmitButtonTarget ? this.submitButtonTarget : form.querySelector("[type='submit']");
    if (btn) btn.disabled = true;

    try {
      // Ensure THIS form gets a fresh v3 token and the hidden input is filled
      const token = await recaptchaGetFreshTokenFor("submit_demo_form", form);
      const recaptchaInput = form.querySelector(
        'input[name="g-recaptcha-response-data[submit_demo_form]"],textarea[name="g-recaptcha-response-data[submit_demo_form]"]'
      );
      if (!token || !recaptchaInput?.value) {
        alert("We couldn't verify you’re human. Please refresh the page and try again.");
        return;
      }

      // Build payload and submit
      const formData = new FormData(form);
      formData.append("pageUri", window.location.href);
      formData.append("pageName", document.title);
      formData.append("hutk", localStorage.getItem("hutk") || "");

      const response = await fetch(form.action, {
        method: "POST",
        headers: { "X-CSRF-Token": formData.get("authenticity_token") },
        body: formData,
      });

      const ct = response.headers.get("content-type") || "";
      const result = ct.includes("application/json") ? await response.json() : null;

      if (!response.ok) throw new Error(result?.message || `HTTP ${response.status}`);

      if (result?.status === "ok") {
        this.logHubSpotPageView();
        this.loadRevenueHeroWidget?.(result.hero_data?.router_id, result.hero_data?.fields);
      } else {
        console.warn("[RESULT] server message:", result?.message);
      }
    } catch (err) {
      console.error("[FORM] submitForm error:", err);
      alert("Something went wrong submitting the form. Please refresh the page and try again.");
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // ----- UI helpers -----
  logHubSpotPageView() {
    if (typeof window._hsq === "undefined") {
      setTimeout(() => this.logHubSpotPageView(), 500);
      return;
    }
    window._hsq.push(['setPath', window.location.pathname + window.location.search]);
    window._hsq.push(['trackPageView']);
  }


  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    if (this.hasProductCheckboxTarget) {
      this.productCheckboxTargets.forEach(cb => (cb.checked = this.allSelected));
    }
    if (this.hasSelectAllButtonTarget) {
      this.selectAllButtonTarget.textContent = this.allSelected ? "Deselect All" : "Select All";
    }
  }

  // Keep these for parity with your original controller
  isAtLeastOneChecked() {
    return this.productCheckboxTargets?.some?.(cb => cb.checked) || true;
  }

  showErrorMessage() {
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.remove("hidden");
  }

  hideErrorMessage() {
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.add("hidden");
  }

  loadRevenueHeroWidget(routerId, fields) {
    try {
      const hero = new RevenueHero({ routerId, showLoader: true });
      hero.submit(fields).then(rhData => hero.dialog.open(rhData)).catch(e => console.error("Error with RevenueHero:", e));
    } catch (e) {
      console.error("Error with RevenueHero:", e);
    }
  }
}